import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useStepUp } from "@/context/StepUp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import PivotPoints from "@/components/PivotPoints";
import { AlertTriangle, ShieldAlert, Loader2, Trash2, Mail, Smartphone, CheckCircle2, CheckSquare, Square, Download, Archive, Upload, RotateCcw, Lock } from "lucide-react";

const KEEP = ["Program modülleri (kod, ekranlar, menüler)", "En önemli 5 kullanıcı (yönetici dahil)", "Roller & yetkiler", "Sabit/temel sistem ayarları & tasarım",
  "Entegrasyon anahtarları (AI, e-Belge, pazaryeri)", "Döviz & yakıt ayarları", "Yüklenen logo/dosyalar"];

export default function SystemReset() {
  const { request: stepUp } = useStepUp();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [meta, setMeta] = useState(null);
  const [emailCode, setEmailCode] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [stdBusy, setStdBusy] = useState(false);
  const [std, setStd] = useState(null);

  useEffect(() => {
    api.get("/system/reset/groups").then((r) => {
      const g = r.data.groups || [];
      setGroups(g); setSelected(new Set(g.map((x) => x.key)));
    }).catch(() => {});
  }, []);

  const loadStd = () => api.get("/sectors/standards-status").then((r) => setStd(r.data)).catch(() => {});
  useEffect(() => { loadStd(); }, []);
  const [lockStd, setLockStd] = useState(true);
  useEffect(() => { api.get("/settings/catalog-policy").then((r) => setLockStd(r.data.lock_standard_products !== false)).catch(() => {}); }, []);
  const toggleLock = async (v) => {
    setLockStd(v);
    try { await api.put("/settings/catalog-policy", { lock_standard_products: v }); toast.success(v ? "Standart Kilit açıldı — standart ürünler silinemez" : "Standart Kilit kapatıldı — standart ürünler silinebilir"); }
    catch (e) { setLockStd(!v); toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
  };

  const toggle = (key) => setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const allOn = groups.length > 0 && selected.size === groups.length;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(groups.map((g) => g.key)));

  const startReset = async () => {
    if (selected.size === 0) return toast.error("En az bir sıfırlama grubu seçin");
    let t;
    try { t = await stepUp("Sistem Sıfırlama"); }
    catch { return; } // vazgeçildi
    setBusy(true);
    try {
      const { data } = await api.post("/system/reset/request", {}, { headers: { "X-Step-Up": t } });
      setToken(t); setMeta(data); setEmailCode(""); setSmsCode(""); setConfirmText(""); setResult(null); setOpen(true);
      toast.success("Doğrulama kodları oluşturuldu");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İstek başarısız"); }
    finally { setBusy(false); }
  };

  const confirmReset = async () => {
    if (!emailCode.trim() || !smsCode.trim()) return toast.error("Hem e-posta hem SMS kodunu girin");
    if (confirmText.trim().toLocaleUpperCase("tr") !== "SIFIRLA") return toast.error('Onay için kutuya "SIFIRLA" yazın');
    setBusy(true);
    try {
      const { data } = await api.post("/system/reset/confirm",
        { email_code: emailCode.trim(), sms_code: smsCode.trim(), groups: [...selected] }, { headers: { "X-Step-Up": token } });
      setResult(data); setOpen(false); setMeta(null);
      toast.success(`Sistem sıfırlandı — ${data.total_cleared} kayıt temizlendi. Sayfa yenileniyor…`);
      setTimeout(() => window.location.reload(), 2600);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Sıfırlama başarısız"); }
    finally { setBusy(false); }
  };

  const exportBackup = async () => {
    setExporting(true);
    try {
      const res = await api.get("/system/backup/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url; a.download = `groot-yedek-${ts}.zip`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Fiziki yedek indirildi (ZIP)");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Yedek indirilemedi"); }
    finally { setExporting(false); }
  };

  const restoreBackup = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    e.target.value = "";
    if (!window.confirm("DİKKAT: Bu ZIP yedeği geri yüklenecek ve mevcut veriler yedekteki içerikle DEĞİŞTİRİLECEK. Yanlışlıkla silinen verileri kurtarmak için kullanın. Devam edilsin mi?")) return;
    setRestoring(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/system/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Geri yükleme tamam — ${data.total_documents} kayıt · ${data.collections} koleksiyon. Sistem kaldığı yerden devam ediyor, sayfa yenileniyor…`);
      setTimeout(() => window.location.reload(), 2800);
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail) || "Geri yükleme başarısız"); }
    finally { setRestoring(false); }
  };

  const restoreStandards = async () => {
    if (!window.confirm("Tüm sektörlerin STANDART kategori ve ürünleri şablondan geri yüklenecek. Mevcut verileriniz SİLİNMEZ; yalnızca eksik standartlar geri gelir ve daha önce üretilmiş standart AI görselleri yeniden bağlanır. Devam edilsin mi?")) return;
    setStdBusy(true);
    try {
      const { data } = await api.post("/sectors/restore-standards", {});
      toast.success(data.message || "Standartlar arka planda geri yükleniyor");
      setStd((s) => ({ ...(s || {}), running: true, done_sectors: 0, total_sectors: data.sectors || 0 }));
      const poll = setInterval(loadStd, 2000);
      setTimeout(() => clearInterval(poll), 90000);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Geri yükleme başlatılamadı"); }
    finally { setStdBusy(false); }
  };

  const wipesLocations = selected.has("locations");

  return (
    <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 p-6 shadow-sm" data-testid="system-reset-panel-inner">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-red-700">
        <ShieldAlert className="h-5 w-5" /> Sistem Sıfırlama (Tehlikeli Bölge)
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Aşağıdan <b>hangi verilerin sıfırlanacağını seçin</b>; seçmedikleriniz korunur. Program modülleri, sabit ayarlar ve tasarım her zaman korunur.
        <b> Sıfırlama başlamadan önce TÜM veriler otomatik yedeklenir</b> (obje deposu + bağlıysa GitHub); yedek alınamazsa işlem iptal edilir.
        Bu işlem <b>geri alınamaz</b>. Yalnızca <b>Yönetici</b> yapabilir ve <b>çift doğrulama</b> (e-posta + SMS kodu) gerektirir.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="system-backup-panel">
        <div className="min-w-[200px] flex-1">
          <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-800"><Archive className="h-4 w-4" /> Fiziki Yedek / Dışa Aktarım</p>
          <p className="text-[12px] text-slate-600">Tüm verileri (bütün koleksiyonlar, JSON) tek bir <b>ZIP</b> dosyası olarak indirin. Sıfırlamadan önce veya düzenli fiziki yedek için kullanın. Yanlışlıkla silerseniz <b>ZIP'ten Geri Yükle</b> ile sistem kaldığı yerden devam eder.</p>
        </div>
        <Button data-testid="system-backup-export" onClick={exportBackup} disabled={exporting}
          className="bg-emerald-600 text-white hover:bg-emerald-700">
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Projeyi Dışa Aktar (ZIP)
        </Button>
        <label className="cursor-pointer">
          <input type="file" accept=".zip,application/zip" className="hidden" onChange={restoreBackup} data-testid="system-backup-restore-input" />
          <span data-testid="system-backup-restore" className="inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50">
            {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} ZIP'ten Geri Yükle
          </span>
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-sky-200 bg-sky-50/60 p-3" data-testid="standards-restore-panel">
        <div className="min-w-[200px] flex-1">
          <p className="flex items-center gap-1.5 text-sm font-bold text-sky-800"><RotateCcw className="h-4 w-4" /> Standartları Geri Yükle (Sistem Ayarlarına Dön)</p>
          <p className="text-[12px] text-slate-600">Sektörlerin <b>standart kategori/ürünleri</b> her zaman kalıcıdır. Kullanıcılar tek tek silse bile buradan <b>tek tıkla</b> hepsi şablondan geri gelir; daha önce üretilmiş <b>standart AI görselleri</b> de yeniden bağlanır. Mevcut verileriniz <b>silinmez</b>.</p>
          {std && <p className="mt-1 text-[11px] font-semibold text-sky-700" data-testid="standards-status">Şablonda <b>{std.standard_products}</b> standart (kalıcı) ürün · <b>{std.standard_categories}</b> kategori · <b>{std.standard_images}</b> kayıtlı standart AI görseli{std.running ? ` · geri yükleniyor (${std.done_sectors}/${std.total_sectors})…` : ""}</p>}
          <p className="mt-0.5 text-[10px] text-slate-400">Not: Bu sayaç yalnızca <b>standart şablon</b> ürünleri gösterir; toplam kataloğunuzda çok daha fazla ürün olabilir (kendi eklediğiniz ürünler bundan bağımsızdır ve silinmez).</p>
        </div>
        <Button data-testid="restore-standards-btn" onClick={restoreStandards} disabled={stdBusy || (std && std.running)}
          className="bg-sky-600 text-white hover:bg-sky-700">
          {(stdBusy || (std && std.running)) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />} Standartları Geri Yükle
        </Button>
        <div className="flex w-full items-center justify-between gap-3 border-t border-sky-200 pt-2.5" data-testid="standard-lock-row">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[13px] font-bold text-slate-700"><Lock className="h-3.5 w-3.5 text-sky-600" /> Standart Kilit</p>
            <p className="text-[11px] text-slate-500">Açıkken <b>standart ürünler silinemez</b> (katalog her zaman dolu kalır). Kapatırsanız kullanıcı standart ürünleri de silebilir.</p>
          </div>
          <Switch data-testid="standard-lock-switch" checked={lockStd} onCheckedChange={toggleLock} />
        </div>
      </div>

      <div className="mt-4" data-testid="system-reset-pivot"><PivotPoints /></div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg bg-white p-3 ring-1 ring-inset ring-red-200">
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-red-600"><Trash2 className="h-3.5 w-3.5" /> Sıfırlanacakları Seç</p>
            <button type="button" data-testid="reset-toggle-all" onClick={toggleAll} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-red-600">
              {allOn ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />} {allOn ? "Tümünü kaldır" : "Tümünü seç"}
            </button>
          </div>
          <div className="space-y-1" data-testid="reset-groups">
            {groups.length === 0 && <p className="text-[13px] text-slate-400">Yükleniyor…</p>}
            {groups.map((g) => {
              const on = selected.has(g.key);
              return (
                <button key={g.key} type="button" data-testid={`reset-group-${g.key}`} onClick={() => toggle(g.key)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors ${on ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200" : "text-slate-500 hover:bg-slate-50"}`}>
                  {on ? <CheckSquare className="h-4 w-4 shrink-0 text-red-500" /> : <Square className="h-4 w-4 shrink-0 text-slate-300" />}
                  <span>{g.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="rounded-lg bg-white p-3 ring-1 ring-inset ring-emerald-200">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Her Zaman Korunanlar</p>
          <ul className="space-y-1 text-[13px] text-slate-600">{KEEP.map((x) => <li key={x} className="flex items-center gap-1.5"><span className="h-1 w-1 rounded-full bg-emerald-400" /> {x}</li>)}</ul>
          {wipesLocations && <p className="mt-3 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700 ring-1 ring-inset ring-amber-200">Depo/Mağaza seçili — silindikten sonra POS/stok için 1 Test Depo + 1 Test Mağaza otomatik yeniden oluşturulur.</p>}
        </div>
      </div>

      {result && (
        <div data-testid="reset-result" className="mt-4 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700 ring-1 ring-inset ring-emerald-200">
          Sistem sıfırlandı — toplam {result.total_cleared} kayıt temizlendi.
          {result.backup && <span className="block text-[12px] text-emerald-600">Yedek alındı: {result.backup.total_documents} kayıt · {result.backup.filename}{result.backup.github_url ? " · GitHub'a yüklendi" : ""}</span>}
          <span className="block">Sayfa yenileniyor…</span>
        </div>
      )}

      <Button data-testid="system-reset-start" onClick={startReset} disabled={busy || selected.size === 0}
        className="mt-5 bg-red-600 text-white hover:bg-red-700">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-2 h-4 w-4" />} Seçili Verileri Sıfırla ({selected.size} grup)
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent data-testid="system-reset-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-red-700"><ShieldAlert className="h-5 w-5" /> Çift Doğrulama ile Onayla</DialogTitle>
            <DialogDescription>
              Hesabınıza gönderilen <b>iki kodu</b> girin. Seçtiğiniz <b>{selected.size} grup</b> kalıcı silinecek.
            </DialogDescription>
          </DialogHeader>
          {meta && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 p-3 text-[12px] text-slate-600 ring-1 ring-inset ring-slate-200">
                <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-brand-green" /> E-posta: <b>{meta.email_masked}</b> {meta.email_sent ? <span className="text-emerald-600">· gönderildi</span> : <span className="text-amber-600">· gönderilemedi</span>}</p>
                <p className="mt-1 flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-brand-green" /> SMS: <b>{meta.phone_masked || "telefon tanımsız"}</b> {meta.sms_sent ? <span className="text-emerald-600">· gönderildi</span> : <span className="text-amber-600">· gönderilemedi (MOCKED)</span>}</p>
              </div>

              {(meta.email_code_fallback || meta.sms_code_fallback) && (
                <div data-testid="reset-fallback-codes" className="rounded-lg bg-amber-50 p-3 text-[12px] text-amber-800 ring-1 ring-inset ring-amber-200">
                  <p className="font-semibold">⚠ Bazı kanallar yapılandırılmadı — kodlar burada gösteriliyor (yalnız test/geliştirme):</p>
                  {meta.email_code_fallback && <p className="mt-1 font-mono">E-posta kodu: <b>{meta.email_code_fallback}</b></p>}
                  {meta.sms_code_fallback && <p className="font-mono">SMS kodu: <b>{meta.sms_code_fallback}</b> (Twilio anahtarı eklenince gerçek SMS gider)</p>}
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">E-posta Kodu</label>
                <Input data-testid="reset-email-code" inputMode="numeric" maxLength={6} value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))} placeholder="6 haneli kod" className="tabular tracking-widest" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">SMS Kodu</label>
                <Input data-testid="reset-sms-code" inputMode="numeric" maxLength={6} value={smsCode} onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ""))} placeholder="6 haneli kod" className="tabular tracking-widest" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-red-600">Son onay — kutuya <b>SIFIRLA</b> yazın</label>
                <Input data-testid="reset-confirm-text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="SIFIRLA" />
              </div>
              <div className="flex items-center gap-2 rounded-md bg-red-50 p-2.5 text-[11px] text-red-700 ring-1 ring-inset ring-red-200">
                <AlertTriangle className="h-4 w-4 shrink-0" /> Bu işlem geri alınamaz. Seçtiğiniz veriler silinecek.
              </div>
              <Button data-testid="reset-confirm-btn" onClick={confirmReset} disabled={busy} className="w-full bg-red-600 text-white hover:bg-red-700">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Kalıcı Olarak Sıfırla
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
